/**
 * Logo List Carousel - 参考 test.js 品牌轮播实现方式
 * 支持双向滚动、拖拽、性能优化等功能
 */

(function() {
  'use strict';

  // ==================== 配置 ====================
  const CONFIG = {
    mobileBreakpoint: 768,
    visibilityCacheDuration: 200, // ms
    defaultSpeed: 60 // px/s
  };

  // ==================== 工具函数 ====================
  const Utils = {
    isMobile() {
      return window.innerWidth <= CONFIG.mobileBreakpoint;
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  };

  // ==================== Logo List Carousel ====================
  class LogoListCarousel {
    constructor(viewport, track) {
      this.viewport = viewport;
      this.track = track;
      this.direction = (viewport.dataset.direction || 'left') === 'right' ? 1 : -1;
      this.speedPxPerSec = parseInt(viewport.dataset.speed || CONFIG.defaultSpeed, 10);
      
      this.items = track.querySelectorAll('.mt-logo-list__item');
      this.baseItemCount = Math.max(1, this.items.length);
      this.rafId = null;
      this.offset = 0;
      this.loopDistance = 0;
      this.lastX = null;
      this.cachedVisibility = true;
      this.visibilityCacheTime = 0;
      
      // 拖拽状态
      this.isDragging = false;
      this.startX = 0;
      this.dragStartOffset = 0;
    }

    async init() {
      if (this.items.length === 0) return;

      await this.waitForImages();
      this.computeLoopDistance();
      this.setupDragHandlers();
      this.startLoop();
      this.setupResizeHandler();
    }

    async waitForImages() {
      const images = this.track.querySelectorAll('.mt-logo-list__image');
      if (images.length === 0) return;

      const loadPromises = Array.from(images).map(img => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      });

      await Promise.all(loadPromises);
      await Utils.delay(100);
    }

    computeLoopDistance() {
      const style = getComputedStyle(this.track);
      const gap = parseInt(style.gap || '0', 10);
      const viewportWidth = window.innerWidth || 1024;
      
      // 获取第一个 group 中的所有 items
      const groups = this.track.querySelectorAll('.mt-logo-list__group');
      const firstGroup = groups[0];
      
      if (!firstGroup) {
        // 如果没有 group，直接使用所有 items
        const allItems = this.track.querySelectorAll('.mt-logo-list__item');
        if (allItems.length === 0) return;
        
        const groupWidth = Array.from(allItems).reduce((w, item, idx) => {
          return w + item.offsetWidth + (idx < allItems.length - 1 ? gap : 0);
        }, 0);
        
        this.loopDistance = groupWidth + gap;
        this.offset = this.direction > 0 ? -this.loopDistance : 0;
        this.updateTransform();
        return;
      }

      const baseItems = Array.from(firstGroup.querySelectorAll('.mt-logo-list__item'));
      if (baseItems.length === 0) return;

      const groupWidth = baseItems.reduce((w, item, idx) => {
        return w + item.offsetWidth + (idx < baseItems.length - 1 ? gap : 0);
      }, 0);

      if (groupWidth === 0) return;

      // 克隆足够的内容以实现无缝循环
      const minTotalWidth = Math.max(viewportWidth * 3, groupWidth * 3);
      const neededGroups = Math.ceil(minTotalWidth / groupWidth);
      const currentGroups = groups.length;

      // 如果已经有克隆的 group，先移除
      const cloneGroups = this.track.querySelectorAll('.mt-logo-list__group--clone');
      cloneGroups.forEach(group => group.remove());

      // 克隆足够的 group
      for (let i = currentGroups; i < neededGroups && i < 20; i++) {
        const clonedGroup = firstGroup.cloneNode(true);
        clonedGroup.classList.add('mt-logo-list__group--clone');
        clonedGroup.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clonedGroup);
      }

      this.loopDistance = groupWidth + gap;
      this.offset = this.direction > 0 ? -this.loopDistance : 0;
      this.updateTransform();
    }

    updateTransform() {
      const x = Math.round(this.offset);
      if (this.lastX !== x) {
        this.track.style.transform = `translate3d(${x}px,0,0)`;
        this.track.style.webkitTransform = `translate3d(${x}px,0,0)`;
        this.track.style.willChange = 'transform';
        this.lastX = x;
      }
    }

    startLoop() {
      if (this.rafId) return;
      this.lastTs = null;
      this.rafId = requestAnimationFrame((t) => this.loop(t));
    }

    stopLoop() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    loop(ts) {
      if (!this.lastTs) this.lastTs = ts;
      const dt = (ts - this.lastTs) / 1000;
      this.lastTs = ts;

      // 如果正在拖拽，不执行自动滚动
      if (this.isDragging) {
        this.rafId = requestAnimationFrame((t) => this.loop(t));
        return;
      }

      // 移动端可见性检查
      if (Utils.isMobile()) {
        const now = performance.now();
        if ((now - this.visibilityCacheTime) > CONFIG.visibilityCacheDuration) {
          const rect = this.viewport.getBoundingClientRect();
          const vh = window.innerHeight || 0;
          this.cachedVisibility = rect.bottom > -vh * 0.5 && rect.top < vh * 1.5;
          this.visibilityCacheTime = now;
        }
        if (!this.cachedVisibility) {
          this.rafId = requestAnimationFrame((t) => this.loop(t));
          return;
        }
      }

      this.offset += this.direction * this.speedPxPerSec * dt;

      if (this.loopDistance === 0) {
        this.computeLoopDistance();
        if (this.loopDistance === 0) {
          this.rafId = requestAnimationFrame((t) => this.loop(t));
          return;
        }
      }

      // 循环重置
      if (this.loopDistance > 0) {
        if (this.direction < 0) {
          // 向左滚动：从0减少到-loopDistance，当offset超过-loopDistance时重置到0
          while (this.offset <= -this.loopDistance) {
            this.offset += this.loopDistance;
          }
        } else {
          // 向右滚动：从-loopDistance增加到0，当offset达到或超过0时重置到-loopDistance
          while (this.offset >= 0) {
            this.offset -= this.loopDistance;
          }
        }
      }

      this.updateTransform();
      this.rafId = requestAnimationFrame((t) => this.loop(t));
    }

    setupDragHandlers() {
      const onDragStart = (e) => {
        this.isDragging = true;
        this.startX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
        this.dragStartOffset = this.offset;
        this.stopLoop();
      };

      const onDragMove = (e) => {
        if (!this.isDragging) return;
        e.preventDefault();
        
        const currentX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
        const diff = currentX - this.startX;
        this.offset = this.dragStartOffset + diff;
        
        // 处理循环边界
        if (this.loopDistance > 0) {
          if (this.direction < 0 && this.offset <= -this.loopDistance) {
            this.offset = 0;
          } else if (this.direction > 0 && this.offset >= 0) {
            this.offset = -this.loopDistance;
          }
        }

        this.updateTransform();
      };

      const onDragEnd = () => {
        if (!this.isDragging) return;
        this.isDragging = false;
        // 恢复自动滚动
        if (!this.rafId) {
          this.lastTs = null;
          this.startLoop();
        }
      };

      this.viewport.addEventListener('mousedown', onDragStart);
      this.viewport.addEventListener('mousemove', onDragMove);
      this.viewport.addEventListener('mouseup', onDragEnd);
      this.viewport.addEventListener('mouseleave', onDragEnd);
      this.viewport.addEventListener('touchstart', onDragStart, { passive: false });
      this.viewport.addEventListener('touchmove', onDragMove, { passive: false });
      this.viewport.addEventListener('touchend', onDragEnd);
    }

    setupResizeHandler() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.loopDistance = 0;
          this.computeLoopDistance();
        }, 250);
      });
    }

    destroy() {
      this.stopLoop();
      // 移除事件监听器（简化处理，实际项目中可以保存引用）
    }
  }

  // ==================== 初始化 ====================
  function initViewport(viewport) {
    const track = viewport.querySelector('[data-carousel-track]');
    if (!track) return;

    const carousel = new LogoListCarousel(viewport, track);
    carousel.init();
  }

  // DOM 加载完成后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('.mt-logo-list [data-carousel-viewport]').forEach(initViewport);
    });
  } else {
    document.querySelectorAll('.mt-logo-list [data-carousel-viewport]').forEach(initViewport);
  }

})();
